import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { AppBar, Toolbar, IconButton, Box, Typography, Avatar, Menu, MenuItem, ListItemIcon, ListItemText, Divider, Badge, Chip } from '@mui/material';
import { Menu as MenuIcon } from 'lucide-react';
import { Icon } from '@iconify/react';
interface NavbarProps {
  onMenuClick: () => void;
}
const Navbar: React.FC<NavbarProps> = ({
  onMenuClick
}) => {
  const {
    user,
    logout
  } = useAuth();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleAvatarClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleLogout = () => {
    handleClose();
    logout();
    navigate('/login');
  };
  const handleChangePassword = () => {
    handleClose();
    navigate('/change-password');
  };
  const getRoleColor = (role?: string) => {
    switch (role?.toLowerCase()) {
      case 'maker':
        return '#0052CC';
      case 'checker':
        return '#0B7923';
      case 'admin':
        return '#B38600';
      default:
        return '#6B7280';
    }
  };
  return <AppBar position="static" elevation={0} sx={{
    bgcolor: '#FFFFFF',
    borderBottom: '1px solid #E5E7EB',
    zIndex: theme => theme.zIndex.drawer + 1
  }} data-id="element-836">
      <Toolbar sx={{
      justifyContent: 'space-between',
      px: {
        xs: 2,
        sm: 3
      }
    }} data-id="element-837">
        {/* Left side - Menu button (mobile) + Logo */}
        <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }} data-id="element-838">
          <IconButton edge="start" onClick={onMenuClick} sx={{
          display: {
            xs: 'flex',
            lg: 'none'
          },
          color: 'text.secondary',
          '&:hover': {
            bgcolor: 'action.hover'
          }
        }} data-id="element-839">
            <MenuIcon size={24} data-id="element-840" />
          </IconButton>
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/xyh4FinweYvNRM6XVnTFSv/openRemitLogo-1.png" alt="OpenRemit Logo" style={{
          height: 32,
          width: 'auto'
        }} data-id="element-841" />
        </Box>
        {/* Right side - User avatar with dropdown */}
        <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: {
          xs: 1,
          sm: 2
        }
      }} data-id="element-842">
          {user?.branchCode && <Chip label={`BR-${user.branchCode}`} size="small" sx={{
          bgcolor: '#F3F5F7',
          color: '#374151',
          fontWeight: 600,
          fontSize: '0.75rem',
          display: {
            xs: 'none',
            sm: 'flex'
          }
        }} data-id="element-843" />}

          <IconButton onClick={handleAvatarClick} sx={{
          p: 0.5,
          '&:hover': {
            bgcolor: '#F9FAFB'
          }
        }} data-id="element-844">
            <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 1,
            py: 0.5,
            borderRadius: 2
          }} data-id="element-845">
              <Avatar sx={{
              width: 36,
              height: 36,
              bgcolor: '#E8F0FE',
              color: '#0052CC',
              fontSize: '0.875rem',
              fontWeight: 600
            }} data-id="element-846">
                {user?.name?.charAt(0).toUpperCase() || 'U'}
              </Avatar>
              <Box sx={{
              display: {
                xs: 'none',
                md: 'flex'
              },
              flexDirection: 'column',
              alignItems: 'flex-start'
            }} data-id="element-847">
                <Typography variant="body2" sx={{
                color: '#1F2937',
                fontWeight: 600,
                lineHeight: 1.2
              }} data-id="element-848">
                  {user?.name || 'User'}
                </Typography>
                <Typography variant="caption" sx={{
                color: getRoleColor(user?.role),
                fontWeight: 600,
                textTransform: 'capitalize'
              }} data-id="element-849">
                  {user?.role || 'User'}
                </Typography>
              </Box>
            </Box>
          </IconButton>
          <Menu anchorEl={anchorEl} open={open} onClose={handleClose} onClick={handleClose} transformOrigin={{
          horizontal: 'right',
          vertical: 'top'
        }} anchorOrigin={{
          horizontal: 'right',
          vertical: 'bottom'
        }} PaperProps={{
          elevation: 3,
          sx: {
            mt: 1.5,
            minWidth: 180,
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
          }
        }} data-id="element-850">
            <MenuItem onClick={handleChangePassword} sx={{
            py: 1.5,
            px: 2,
            '&:hover': {
              bgcolor: 'action.hover'
            }
          }} data-id="element-851">
              <ListItemIcon data-id="element-852">
                <Icon icon="lucide:lock" width={20} height={20} data-id="element-853" />
              </ListItemIcon>
              <ListItemText primary="Change Password" primaryTypographyProps={{
              fontSize: 14,
              fontWeight: 500
            }} data-id="element-854" />
            </MenuItem>
            <Divider data-id="element-855" />
            <MenuItem onClick={handleLogout} sx={{
            py: 1.5,
            px: 2,
            '&:hover': {
              bgcolor: 'action.hover'
            }
          }} data-id="element-856">
              <ListItemIcon data-id="element-857">
                <Icon icon="lucide:log-out" data-id="element-858" />
              </ListItemIcon>
              <ListItemText primary="Logout" primaryTypographyProps={{
              fontSize: 14,
              fontWeight: 500
            }} data-id="element-859" />
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>;
};
export default Navbar;